import React from 'react';
import {
  MainKPIDetailItem,
  SpecificKPI,
} from '../api/company/companies.models';
import { MainKPIItem } from '../models/analytics.models';
import SpecificKPIGaugeChart from './SpecificKPIGaugeChart';
import CompletionProgressBar from './CompletionProgressBar';
import { useLanguage } from '../locale';
import { useFeedbackCategories } from '../hooks/useFeedbackCategories';

interface ProcessedKPIItem extends MainKPIItem {
  gaugeIndicator: number;
  mappedSentiment: 'positive' | 'negative' | 'neutral';
}

interface MainKPIDetailsPanelProps {
  mainKPI: MainKPIDetailItem;
  mainKPIParentData?: ProcessedKPIItem;
  specificKPIs: MainKPIDetailItem[];
  specificKPITitles: SpecificKPI[];
  registeredUsers: number;
  onClose: () => void;
}

const MainKPIDetailsPanel: React.FC<MainKPIDetailsPanelProps> = ({
  mainKPI,
  mainKPIParentData,
  specificKPIs,
  specificKPITitles,
  registeredUsers,
  onClose,
}) => {
  const [t] = useLanguage();
  const feedbackCategories = useFeedbackCategories();

  // Function to get category title from feedback categories config (same as AreasSection)
  const getCategoryTitle = (categoryId: string) => {
    const category = feedbackCategories.find((cat) => cat.id === categoryId);
    return category ? category.title : categoryId.replace('#', '');
  };

  // Function to get specific KPI title
  const getSpecificKPITitle = (kpiId: string) => {
    const found = specificKPITitles.find((kpi) => kpi.id === kpiId);
    return found ? found.title : kpiId.replace('#', '');
  };

  // Function to calculate completion percentage
  const getCompletionPercentage = (completedCount: number) => {
    if (registeredUsers === 0) return 0;
    return Math.round((completedCount / registeredUsers) * 100);
  };

  // Function to convert score to percentage (relative to 0.5 neutral point)
  const getPercentageFromScore = (score: number) => {
    const percentage = (score - 0.5) * 200;
    return percentage > 0
      ? `+${percentage.toFixed(0)}%`
      : `${percentage.toFixed(0)}%`;
  };

  // Function to get sentiment color and translated label
  const getSentimentInfo = (sentiment: 'positive' | 'negative' | 'neutral') => {
    switch (sentiment) {
      case 'positive':
        return {
          color: 'text-green-600',
          label: t('dashboard.analytics.positive'),
        };
      case 'negative':
        return {
          color: 'text-red-600',
          label: t('dashboard.analytics.negative'),
        };
      case 'neutral':
        return {
          color: 'text-yellow-600',
          label: t('dashboard.analytics.neutral'),
        };
      default:
        return { color: 'text-gray-600', label: 'Unknown' };
    }
  };

  return (
    <div 
      id="main-kpi-details-panel"
      className="mt-4 p-6 bg-gray-50 rounded-lg border border-gray-200"
    >
      {/* Header with close button */}
      <div className="flex justify-between items-start mb-6">
        <div className="flex-1">
          <h3 className="text-xl font-bold text-fake-black mb-3">
            {getCategoryTitle(mainKPI.kpiId)}
          </h3>

          {/* Main KPI Information */}
          {mainKPIParentData && (
            <div className="space-y-2">
              {/* First row: Score, Percentage, Sentiment */}
              <div className="flex items-center space-x-6 text-sm">
                <span className="text-gray-700">
                  {t('dashboard.analytics.kpiDetails.performance')}:{' '}
                  <strong>
                    {mainKPIParentData.score
                      ? getPercentageFromScore(mainKPIParentData.score)
                      : 'N/A'}
                  </strong>
                </span>
                <span className="text-gray-700">
                  {t('dashboard.analytics.kpiDetails.evaluation')}:
                  <strong>
                    <span
                      className={`ml-1 ${
                        getSentimentInfo(mainKPIParentData.categorySentiment)
                          .color
                      }`}
                    >
                      {
                        getSentimentInfo(mainKPIParentData.categorySentiment)
                          .label
                      }
                    </span>
                  </strong>
                </span>
                <span className="text-gray-700">
                  {t('dashboard.analytics.kpiDetails.totalMentions')}:{' '}
                  <strong>{mainKPIParentData.total_mentions}</strong>
                </span>
              </div>
            </div>
          )}
        </div>

        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors ml-4"
          aria-label="Chiudi dettagli"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Specific KPIs Section */}
      <div className="mt-10">
        {/* Section Title */}
        <h4 className="text-lg font-semibold text-fake-black mb-6 text-left">
          {t('dashboard.analytics.kpiDetails.specificKPIs')}
        </h4>

        {/* Content Layout - Grid with Progress Bar and Gauge Charts */}
        <div className="grid grid-cols-3 gap-6 justify-items-center">
          {/* Completion Progress Bar */}
          <div className="flex flex-col items-center">
            <div className="w-full max-w-xs mt-6">
              <CompletionProgressBar
                percentage={getCompletionPercentage(mainKPI.completedCount)}
              />
            </div>
            <div className="text-xs text-fake-black mt-5 text-center max-w-24 truncate">
              {t('dashboard.analytics.kpiDetails.progressBarLabel')}
            </div>
          </div>

          {/* Specific KPIs Charts */}
          {specificKPIs.length > 0 ? (
            specificKPIs.map((kpi) => (
              <div key={kpi.kpiId} className="flex justify-center">
                <SpecificKPIGaugeChart
                  score={kpi.kpiScore}
                  kpiName={getSpecificKPITitle(kpi.kpiId)}
                />
              </div>
            ))
          ) : (
            /* No specific KPIs available */
            <div className="col-span-2 text-center py-8 text-gray-500">
              <p>{t('dashboard.analytics.kpiDetails.noSpecificKPIs')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MainKPIDetailsPanel;
